import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';
import { IqeProjectOperationRequest } from '../components/tools-provisioning/components/IQEAgent/interfaces/api/requests/saveProject.request';
import type { IqeStopRunRequest } from '../components/tools-provisioning/components/IQEAgent/interfaces/api/requests/stopRun.request';
import type {
  IqeRunLogsRequest,
  IqeRunResultsRequest,
} from '../components/tools-provisioning/components/IQEAgent/interfaces/api/requests/runResults.request';
import type {
  ExecutionDashboardResponse,
  TestPlanResponse,
  IqeRunLogsResponse,
} from '../components/tools-provisioning/components/IQEAgent/interfaces/api/responses/runResults.response';
import type { IqeStopRunResponse } from '../components/tools-provisioning/components/IQEAgent/interfaces/api/responses/stopRun.response';
import {
  toIqeRunLogsUrl,
  toIqeRunResultsUrl,
} from '../components/tools-provisioning/components/IQEAgent/dto/requestDto/runResults.request.dto';

export interface IqeProjectApi {
  getIqeProjects: (apmNumber: string) => Promise<any>;
  getIqeProjectStatus: (
    apmNumber: string,
    iqeProjectId: string,
  ) => Promise<any>;
  getIqeProjectRuns: (apmNumber: string, iqeProjectId: string) => Promise<any>;
  getIqeRunLogs: (request: IqeRunLogsRequest) => Promise<IqeRunLogsResponse>;

  getAdoProjectTestPlans: (
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectOrg: string,
  ) => Promise<any>;

  getJiraProjectTestPlans: (
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectKey: string,
  ) => Promise<any>;

  getAdoProjectTestSuites: (
    apmNumber: string,
    projectName: string,
    projectOrg: string,
    testPlanId: string,
    testPlanName: string,
  ) => Promise<any>;

  performProjectOperation: (
    apmNumber: string,
    iqeProjectData: IqeProjectOperationRequest,
  ) => Promise<any>;
  stopRun: (
    apmNumber: string,
    request: IqeStopRunRequest,
  ) => Promise<IqeStopRunResponse>;
  getIqeActivityLogStream: (
    request: IqeRunResultsRequest,
    accessToken: string,
    signal?: AbortSignal,
  ) => Promise<Response>;
  getIqeExecutionDashboard: (
    request: IqeRunResultsRequest,
  ) => Promise<ExecutionDashboardResponse>;
  getIqeTestPlan: (request: IqeRunResultsRequest) => Promise<TestPlanResponse>;
}

export const iqeAgentApiRef = createApiRef<IqeProjectApi>({
  id: 'cbre.devx.api.iqeagent',
});

export class IqeAgentApiImpl implements IqeProjectApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  // IQE Agent

  async getJiraProjectTestPlans(
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectKey: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `/service/iqe/jira/${apmNumber}/testplans`,
        data: {
          projectid: projectId,
          projectname: projectName,
          projectkey: projectKey,
        },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test plans ${err.message}`,
        );
      });
  }

  async getIqeProjects(apmNumber: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/iqe/${apmNumber}/projects`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting APM projects ${err.message}`);
      });
  }

  async getAdoProjectTestPlans(
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectOrg: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/ado/${apmNumber}/testplans`,
        data: {
          projectid: projectId,
          projectname: projectName,
          projectorg: projectOrg,
        },
      })

      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test plans ${err.message}`,
        );
      });
  }

  async getAdoProjectTestSuites(
    apmNumber: string,
    projectName: string,
    projectOrg: string,
    testPlanId: string,
    testPlanName: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/ado/${apmNumber}/testsuites`,
        data: {
          organization: projectOrg,
          project: projectName,
          testPlanId: testPlanId,
          testPlanName: testPlanName,
        },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test suites ${err.message}`,
        );
      });
  }

  // operation is set in the request payload
  async performProjectOperation(
    apmNumber: string,
    iqeProjectData: IqeProjectOperationRequest,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;

    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/${apmNumber}/project`,
        data: { ...iqeProjectData },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while performing IQE project ${iqeProjectData.operation} ${err.message}`,
        );
      });
  }

  async stopRun(
    apmNumber: string,
    request: IqeStopRunRequest,
  ): Promise<IqeStopRunResponse> {
    const instance = await this.axiosInstancePromise;

    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/${apmNumber}/stop`,
        data: { ...request },
      })
      .then(res => res?.data as IqeStopRunResponse)
      .catch(err => {
        throw new Error(
          err?.response?.data?.message ||
            `Error while stopping the run ${err.message}`,
        );
      });
  }

  async getIqeActivityLogStream(
    request: IqeRunResultsRequest,
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<Response> {
    const instance = await this.axiosInstancePromise;
    const url = instance.getUri({ url: toIqeRunResultsUrl(request) });

    const response = await fetch(url, {
      method: 'GET',
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'api-version': '1.0',
        'x-token': 'devx',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Error while getting IQE activity log ${response.status} ${response.statusText}`,
      );
    }

    return response;
  }

  async getIqeExecutionDashboard(
    request: IqeRunResultsRequest,
  ): Promise<ExecutionDashboardResponse> {
    const instance = await this.axiosInstancePromise;
    const url = toIqeRunResultsUrl(request);
    return instance
      .get(url)
      .then(response => response.data as ExecutionDashboardResponse)
      .catch(err => {
        throw new Error(
          err?.response?.data?.message ||
            `Error while getting IQE execution results ${err.message}`,
        );
      });
  }

  async getIqeTestPlan(
    request: IqeRunResultsRequest,
  ): Promise<TestPlanResponse> {
    const instance = await this.axiosInstancePromise;
    const url = toIqeRunResultsUrl(request);
    return instance
      .get(url)
      .then(response => response.data as TestPlanResponse)
      .catch(err => {
        throw new Error(
          err?.response?.data?.message ||
            `Error while getting IQE test plan ${err.message}`,
        );
      });
  }

  async getIqeProjectStatus(
    apmNumber: string,
    iqeProjectId: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/iqe/${apmNumber}/project/${iqeProjectId}/status`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting IQE project status ${err.message}`,
        );
      });
  }

  async getIqeRunLogs(request: IqeRunLogsRequest): Promise<IqeRunLogsResponse> {
    const instance = await this.axiosInstancePromise;
    const response = await instance.get<IqeRunLogsResponse>(
      toIqeRunLogsUrl(request),
    );
    return response.data;
  }

  async getIqeProjectRuns(
    apmNumber: string,
    iqeProjectId: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/iqe/${apmNumber}/project/${iqeProjectId}/runs`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting IQE project runs ${err.message}`);
      });
  }
}
